/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AccountTree as AutoLayoutIcon,
  CheckCircle as ValidIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  FitScreen as FitScreenIcon,
  PlayArrow as RunIcon,
  Save as SaveIcon,
  Schedule as QueuedIcon,
  Search as SearchIcon,
  ShowChart as ShowChartIcon,
  Visibility as ViewIcon,
  Warning as WarningIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import {
  Background,
  BackgroundVariant,
  BaseEdge,
  Edge,
  EdgeLabelRenderer,
  EdgeProps,
  Handle,
  MarkerType,
  MiniMap,
  Node,
  NodeProps,
  Panel,
  Position,
  ReactFlow,
  useReactFlow,
} from '@xyflow/react';

import { expertApi } from '../../services/api';
import type { ExpertListItem } from '../../types';
import { getExpertInputSummary, getInputSourceLabel, getInputTypeLabel } from '../../utils/expertRuntimeInputs';
import ExpertEditorPanel from './ExpertEditorPanel';
import { EXPERT_NODE_WIDTH, getLayoutedElements } from '../../utils/systemGraphLayout';

interface ValidationStatus {
  hasError?: boolean;
  hasWarning?: boolean;
}

export interface ValidationIssue {
  type: 'error' | 'warning' | 'info';
  nodeId?: string;
  message: string;
}

export type CanvasExpertRunStatus = 'pending' | 'running' | 'completed' | 'skipped' | 'error';

export interface CanvasExpertRunState {
  status: CanvasExpertRunStatus;
  executionId?: string | null;
  totalCost?: number;
  result?: Record<string, any>;
}

const getExpertTypeColor = (expertType?: string): 'primary' | 'warning' | 'info' | 'secondary' => {
  if (expertType === 'ANALYSIS') return 'primary';
  if (expertType === 'CODE') return 'warning';
  if (expertType === 'AGENT') return 'info';
  return 'secondary';
};

const getExpertRuntimeLabel = (expert: ExpertListItem) => {
  if (expert.expert_type === 'AGENT') return expert.agent_name || 'Agent not set';
  if (expert.expert_type === 'CODE') return 'Python code';
  return expert.model_name || 'Model not set';
};

const getRunStatusColor = (status?: CanvasExpertRunStatus) => {
  switch (status) {
    case 'running':
      return 'primary.main';
    case 'completed':
      return 'success.main';
    case 'error':
      return 'error.main';
    case 'skipped':
      return 'warning.main';
    case 'pending':
      return 'warning.main';
    default:
      return undefined;
  }
};

const getRunStatusBackground = (status?: CanvasExpertRunStatus, isDark?: boolean) => {
  switch (status) {
    case 'running':
      return isDark ? 'rgba(25, 118, 210, 0.14)' : 'rgba(25, 118, 210, 0.06)';
    case 'completed':
      return isDark ? 'rgba(46, 125, 50, 0.12)' : 'rgba(46, 125, 50, 0.05)';
    case 'error':
      return isDark ? 'rgba(211, 47, 47, 0.14)' : 'rgba(211, 47, 47, 0.06)';
    case 'skipped':
    case 'pending':
      return isDark ? 'rgba(237, 108, 2, 0.1)' : 'rgba(237, 108, 2, 0.05)';
    default:
      return undefined;
  }
};

const getRunStatusLabel = (status?: CanvasExpertRunStatus) => {
  switch (status) {
    case 'running':
      return 'Running';
    case 'completed':
      return 'Done';
    case 'error':
      return 'Failed';
    case 'skipped':
      return 'Skipped';
    case 'pending':
      return 'Queued';
    default:
      return '';
  }
};

const ExpertNode: React.FC<NodeProps> = ({ data, selected }) => {
  const expert = data.expert as ExpertListItem;
  const validation = (data.validation as ValidationStatus) || {};
  const isDark = (data.isDark as boolean) || false;
  const hasIndicatorSets = (data.hasIndicatorSets as boolean) || false;
  const indicatorSetCount = (data.indicatorSetCount as number) || 0;
  const runState = data.runState as CanvasExpertRunState | undefined;
  const onViewExpertRun = data.onViewExpertRun as ((expertId: number) => void) | undefined;
  const runStatus = runState?.status;
  const runStatusColor = getRunStatusColor(runStatus);
  const runStatusBackground = getRunStatusBackground(runStatus, isDark);
  const inputSummary = getExpertInputSummary(expert.input_data);
  const canViewRunDetails = Boolean(
    runState?.executionId && runStatus && !['pending', 'running'].includes(runStatus) && onViewExpertRun
  );

  const renderNodeStatus = () => {
    if (runStatus) {
      const label = getRunStatusLabel(runStatus);

      return (
        <Box display="flex" alignItems="center" gap={0.25} flexShrink={0}>
          <Tooltip title={label}>
            <Box display="flex" alignItems="center">
              {runStatus === 'running' && <CircularProgress size={18} thickness={5} />}
              {runStatus === 'completed' && <ValidIcon fontSize="small" color="success" />}
              {runStatus === 'error' && <ErrorIcon fontSize="small" color="error" />}
              {runStatus === 'skipped' && <WarningIcon fontSize="small" color="warning" />}
              {runStatus === 'pending' && <QueuedIcon fontSize="small" color="warning" />}
            </Box>
          </Tooltip>
          {canViewRunDetails && (
            <Tooltip title="View execution details">
              <IconButton
                size="small"
                aria-label={`View execution details for ${expert.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onViewExpertRun?.(expert.id);
                }}
                sx={{ width: 24, height: 24 }}
              >
                <ViewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    }

    if (validation.hasError) return <ErrorIcon fontSize="small" color="error" />;
    if (validation.hasWarning) return <WarningIcon fontSize="small" color="warning" />;
    if (expert.is_active) return <ValidIcon fontSize="small" color="success" />;
    return null;
  };

  return (
    <Box
      sx={{
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        border: 2,
        borderColor: selected
          ? 'primary.main'
          : runStatusColor || (validation.hasError
            ? 'error.main'
            : validation.hasWarning
              ? 'warning.main'
              : expert.expert_type === 'ANALYSIS'
                ? 'primary.light'
                : expert.expert_type === 'CODE'
                  ? 'warning.light'
                  : expert.expert_type === 'AGENT'
                    ? 'info.light'
                    : 'secondary.light'),
        background: selected
          ? (isDark ? 'rgba(25, 118, 210, 0.2)' : 'primary.50')
          : runStatusBackground || (isDark ? '#2d2d2d' : '#fff'),
        width: EXPERT_NODE_WIDTH,
        boxSizing: 'border-box',
        overflowWrap: 'anywhere',
        boxShadow: selected
          ? 4
          : runStatus === 'running'
            ? '0 0 0 3px rgba(25, 118, 210, 0.14), 0 8px 18px rgba(25, 118, 210, 0.18)'
          : 2,
        animation: runStatus === 'running'
          ? 'canvas-node-running-pulse 1.35s ease-in-out infinite'
            : undefined,
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 6,
        },
        '&::after': runStatus ? {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 3,
          pointerEvents: 'none',
          background: runStatus === 'running'
            ? 'linear-gradient(90deg, #1976d2 0%, #90caf9 50%, #1976d2 100%)'
            : runStatus === 'completed'
              ? '#2e7d32'
            : runStatus === 'error'
              ? '#d32f2f'
            : '#ed6c02',
          backgroundSize: runStatus === 'running' ? '200% 100%' : undefined,
          animation: runStatus === 'running' ? 'canvas-node-running-bar 0.9s linear infinite' : undefined,
        } : undefined,
        '@keyframes canvas-node-running-pulse': {
          '0%, 100%': {
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.14), 0 8px 18px rgba(25, 118, 210, 0.18)',
          },
          '50%': {
            boxShadow: '0 0 0 6px rgba(25, 118, 210, 0.07), 0 10px 24px rgba(25, 118, 210, 0.22)',
          },
        },
        '@keyframes canvas-node-running-bar': {
          '0%': {
            backgroundPosition: '200% 0',
          },
          '100%': {
            backgroundPosition: '0 0',
          },
        },
      }}
    >
      <Handle
        type="target"
        position={data.layoutDirection === 'TB' ? Position.Top : Position.Left}
        style={{
          background: isDark ? '#888' : '#555',
          width: 12,
          height: 12,
          border: isDark ? '2px solid #333' : '2px solid #fff',
        }}
      />

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1, mr: 1 }}>
          {expert.name}
        </Typography>
        {renderNodeStatus()}
      </Box>

      <Chip
        label={expert.expert_type === 'CODE' || expert.expert_type === 'ACTION' ? `${expert.expert_type === 'CODE' ? 'Native safeguard' : 'Native execution'} · ${expert.expert_type_display || expert.expert_type}` : expert.expert_type_display}
        size="small"
        color={
          expert.expert_type === 'ANALYSIS'
            ? 'primary'
            : expert.expert_type === 'CODE'
              ? 'warning'
              : expert.expert_type === 'AGENT'
                ? 'info'
                : 'secondary'
        }
        sx={{ mb: 1, fontSize: '0.7rem' }}
      />

      <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
        Model: {expert.model_name || 'Not set'}
      </Typography>

      {hasIndicatorSets && (
        <Box sx={{ mb: 0.5 }}>
          <Chip
            icon={<ShowChartIcon />}
            label={`${indicatorSetCount} Indicator Set${indicatorSetCount > 1 ? 's' : ''}`}
            size="small"
            color="info"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Box>
      )}

      {expert.use_cache && (
        <Box sx={{ mb: 0.5 }}>
          <Chip label="Cached" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
        </Box>
      )}

      <Box display="flex" gap={0.5} flexWrap="wrap">
        {expert.is_active ? (
          <Chip label="Active" size="small" color="success" variant="outlined" />
        ) : (
          <Chip label="Inactive" size="small" color="default" variant="outlined" />
        )}
        {expert.dry_run && <Chip label="Dry Run" size="small" color="info" variant="outlined" />}
        <Tooltip
          title={
            inputSummary.total ? (
              <Box sx={{ maxWidth: 300 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                  {inputSummary.hasRuntimePacket ? 'Runtime packet inputs' : 'Configured inputs'}
                  {inputSummary.detail ? `: ${inputSummary.detail}` : ''}
                </Typography>
                {inputSummary.refs.map((ref) => (
                  <Typography key={`${ref.source}-${ref.type}-${ref.name}`} variant="caption" sx={{ display: 'block' }}>
                    {ref.name} - {getInputSourceLabel(ref.source)} {getInputTypeLabel(ref.type)}
                  </Typography>
                ))}
              </Box>
            ) : (
              'No configured inputs'
            )
          }
        >
          <Chip
            label={inputSummary.label}
            size="small"
            color={inputSummary.total ? 'success' : 'warning'}
            variant="outlined"
          />
        </Tooltip>
      </Box>

      <Handle
        type="source"
        position={data.layoutDirection === 'TB' ? Position.Bottom : Position.Right}
        style={{
          background: isDark ? '#888' : '#555',
          width: 12,
          height: 12,
          border: isDark ? '2px solid #333' : '2px solid #fff',
        }}
      />
    </Box>
  );
};

const nodeTypes = {
  expertNode: ExpertNode,
};

const DEFAULT_EDGE_COLOR = '#2196f3';
const SELECTED_EDGE_COLOR = '#ff6b6b';

const EDGE_LANE_SPACING = 34;
const EDGE_PAIR_SPACING = 18;
const EDGE_MAX_LANE_OFFSET = 120;
const EDGE_MIN_CONTROL_DISTANCE = 90;
const EDGE_MAX_CONTROL_DISTANCE = 260;

const clampEdgeOffset = (value: number) =>
  Math.max(-EDGE_MAX_LANE_OFFSET, Math.min(EDGE_MAX_LANE_OFFSET, value));

const getCenteredLaneOffset = (index: number, count: number, spacing: number) => (
  count <= 1 ? 0 : (index - (count - 1) / 2) * spacing
);

const sortEdgesForRouting = (items: Edge[]) => [...items].sort((a, b) => (
  `${a.source}->${a.target}->${a.id}`.localeCompare(`${b.source}->${b.target}->${b.id}`)
));

const getEdgeRouteOffsets = (edges: Edge[]) => {
  const sourceGroups = new Map<string, Edge[]>();
  const targetGroups = new Map<string, Edge[]>();
  const pairGroups = new Map<string, Edge[]>();

  edges.forEach((edge) => {
    sourceGroups.set(edge.source, [...(sourceGroups.get(edge.source) || []), edge]);
    targetGroups.set(edge.target, [...(targetGroups.get(edge.target) || []), edge]);
    const pairKey = `${edge.source}->${edge.target}`;
    pairGroups.set(pairKey, [...(pairGroups.get(pairKey) || []), edge]);
  });

  const offsets = new Map<string, number>();

  edges.forEach((edge) => {
    const sourceSiblings = sortEdgesForRouting(sourceGroups.get(edge.source) || []);
    const targetSiblings = sortEdgesForRouting(targetGroups.get(edge.target) || []);
    const pairSiblings = sortEdgesForRouting(pairGroups.get(`${edge.source}->${edge.target}`) || []);
    const sourceOffset = getCenteredLaneOffset(
      sourceSiblings.findIndex((item) => item.id === edge.id),
      sourceSiblings.length,
      EDGE_LANE_SPACING,
    );
    const targetOffset = getCenteredLaneOffset(
      targetSiblings.findIndex((item) => item.id === edge.id),
      targetSiblings.length,
      EDGE_LANE_SPACING,
    );
    const pairOffset = getCenteredLaneOffset(
      pairSiblings.findIndex((item) => item.id === edge.id),
      pairSiblings.length,
      EDGE_PAIR_SPACING,
    );

    offsets.set(edge.id, clampEdgeOffset((sourceOffset + targetOffset) / 2 + pairOffset));
  });

  return offsets;
};

const getRoutedCanvasEdges = (edges: Edge[]) => {
  const routeOffsets = getEdgeRouteOffsets(edges);
  return edges.map((edge) => ({
    ...edge,
    data: {
      ...(edge.data || {}),
      routeOffset: routeOffsets.get(edge.id) || 0,
    },
  }));
};

const getReadableEdgePath = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  routeOffset,
}: {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition?: Position;
  targetPosition?: Position;
  routeOffset: number;
}) => {
  const vertical = sourcePosition === Position.Bottom || sourcePosition === Position.Top;
  const distance = Math.abs(vertical ? targetY - sourceY : targetX - sourceX);
  const controlDistance = Math.max(
    EDGE_MIN_CONTROL_DISTANCE,
    Math.min(EDGE_MAX_CONTROL_DISTANCE, distance * 0.42),
  );
  const sourceDirection = sourcePosition === Position.Left ? -1 : 1;
  const targetDirection = targetPosition === Position.Right ? 1 : -1;
  const sourceControlX = sourceX + (vertical ? routeOffset : controlDistance * sourceDirection);
  const targetControlX = targetX + (vertical ? routeOffset : controlDistance * targetDirection);
  const sourceControlY = sourceY + (vertical ? controlDistance * (sourcePosition === Position.Top ? -1 : 1) : routeOffset);
  const targetControlY = targetY + (vertical ? controlDistance * (targetPosition === Position.Bottom ? 1 : -1) : routeOffset);
  const labelX = (sourceX + targetX) / 2 + (vertical ? routeOffset : 0);
  const labelY = (sourceY + targetY) / 2 + (vertical ? 0 : routeOffset);

  return {
    edgePath: `M ${sourceX},${sourceY} C ${sourceControlX},${sourceControlY} ${targetControlX},${targetControlY} ${targetX},${targetY}`,
    labelX,
    labelY,
  };
};

const DeletableEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  style,
  markerEnd,
  data,
}) => {
  const { setEdges } = useReactFlow();
  const edgeColor = selected ? SELECTED_EDGE_COLOR : DEFAULT_EDGE_COLOR;
  const routeOffset = typeof data?.routeOffset === 'number' ? data.routeOffset : 0;
  let { edgePath, labelX, labelY } = getReadableEdgePath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    routeOffset,
  });

  const routePoints = data?.activeRoutePoints as { x: number; y: number }[] | undefined;
  if (routePoints && routePoints.length > 2) {
    const interior = routePoints.slice(1, -1);
    edgePath = `M ${sourceX},${sourceY} ${interior.map(point => `L ${point.x},${point.y}`).join(' ')} L ${targetX},${targetY}`;
    const middle = interior[Math.floor(interior.length / 2)];
    labelX = middle.x; labelY = middle.y;
  }

  const onEdgeDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    setEdges((edges) => edges.filter((edge) => edge.id !== id));
  };

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        interactionWidth={24}
        style={{
          ...style,
          strokeWidth: selected ? 3 : 2,
          stroke: edgeColor,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        }}
      />
      {selected && (
        <EdgeLabelRenderer>
          <Box
            sx={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            className="nodrag nopan"
          >
            <IconButton
              size="small"
              onClick={onEdgeDelete}
              sx={{
                backgroundColor: '#ff6b6b',
                color: 'white',
                width: 24,
                height: 24,
                '&:hover': {
                  backgroundColor: '#ff4444',
                },
                boxShadow: 2,
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const edgeTypes = {
  deletable: DeletableEdge,
};

const createEdgeMarker = (color: string) => ({
  type: MarkerType.ArrowClosed,
  width: 20,
  height: 20,
  color,
});

const defaultEdgeOptions = {
  type: 'deletable',
  markerEnd: createEdgeMarker(DEFAULT_EDGE_COLOR),
  style: {
    strokeWidth: 2,
    stroke: DEFAULT_EDGE_COLOR,
  },
  focusable: true,
  deletable: true,
  selectable: true,
  interactionWidth: 20,
};

const selectedEdgeStyle = {
  strokeWidth: 3,
  stroke: SELECTED_EDGE_COLOR,
};

export interface AISystemCanvasProps {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: any;
  onEdgesChange: any;
  onConnect: any;
  onNodeClick: (event: React.MouseEvent, node: Node) => void;
  onAutoLayout: () => void;
  expertsData: any;
  addExpertToCanvas: (expert: ExpertListItem) => void;
  setExpertFormOpen: (open: boolean) => void;
  selectedNode: Node | null;
  setSelectedNode: (node: Node | null) => void;
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  refetchExperts: () => void;
  validationIssues: ValidationIssue[];
  onSaveCanvas?: () => void | Promise<void>;
  isSavingCanvas?: boolean;
  onRunCanvas?: () => void | Promise<void>;
  isRunningCanvas?: boolean;
  canRunCanvas?: boolean;
  runDisabledReason?: string;
  expertRunStates?: Record<string, CanvasExpertRunState>;
  onViewExpertRun?: (expertId: number) => void;
  toolbarActions?: React.ReactNode;
  isMobile?: boolean;
  isDark?: boolean;
}

const AISystemCanvas: React.FC<AISystemCanvasProps> = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onAutoLayout,
  expertsData,
  addExpertToCanvas,
  setExpertFormOpen,
  selectedNode,
  setSelectedNode,
  setNodes,
  setEdges,
  refetchExperts,
  validationIssues,
  onSaveCanvas,
  isSavingCanvas = false,
  onRunCanvas,
  isRunningCanvas = false,
  canRunCanvas = true,
  runDisabledReason,
  expertRunStates,
  onViewExpertRun,
  toolbarActions,
  isMobile = false,
  isDark = false,
}) => {
  const reactFlow = useReactFlow();
  const [searchTerm, setSearchTerm] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const routedEdges = useMemo(() => getRoutedCanvasEdges(edges).map(edge => {
    const route = edge.data?.layoutRoute as any;
    const source = nodes.find(node => node.id === edge.source)?.position;
    const target = nodes.find(node => node.id === edge.target)?.position;
    const matches = route && source && target && source.x === route.source?.x && source.y === route.source?.y &&
      target.x === route.target?.x && target.y === route.target?.y;
    return { ...edge, data: { ...edge.data, activeRoutePoints: matches ? route.points : undefined } };
  }), [edges, nodes]);

  const measuredLayoutKey = useRef('');
  useEffect(() => {
    if (!nodes.length || nodes.some(node => !node.measured?.width || !node.measured?.height)) {
      measuredLayoutKey.current = '';
      return;
    }
    const key = nodes.map(node => node.id).sort().join(',');
    if (measuredLayoutKey.current === key) return;
    measuredLayoutKey.current = key;
    if (nodes.every(node => node.data.automaticLayout)) {
      const layout = getLayoutedElements(nodes, edges, nodes[0]?.data.layoutDirection === 'LR' ? 'LR' : 'TB');
      setNodes(layout.nodes);
      setEdges(layout.edges);
    }
  }, [nodes, edges, setNodes, setEdges]);

  const handleZoomIn = () => reactFlow.zoomIn();
  const handleZoomOut = () => reactFlow.zoomOut();
  const handleFitView = () => reactFlow.fitView({ padding: 0.3, maxZoom: 1 });

  const onInit = useCallback(() => {
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized && nodes.length > 0) {
      const timer = setTimeout(() => {
        reactFlow.fitView({ padding: 0.3, maxZoom: 1 });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isInitialized, nodes.length, reactFlow]);

  const filteredExperts = useMemo(() => {
    if (!expertsData?.results) return [];
    if (!searchTerm) return expertsData.results;

    const term = searchTerm.toLowerCase();
    return expertsData.results.filter((expert: ExpertListItem) =>
      expert.name.toLowerCase().includes(term) ||
      expert.expert_type.toLowerCase().includes(term) ||
      expert.model_name?.toLowerCase().includes(term)
    );
  }, [expertsData, searchTerm]);

  const openExpertEditor = useCallback((expert: ExpertListItem) => {
    const existingNode = nodes.find((node) => node.id === expert.id.toString());
    setSelectedNode(existingNode || ({
      id: `library-${expert.id}`,
      type: 'expertNode',
      position: { x: 0, y: 0 },
      data: { expert, isDark },
    } as Node));

    if (isMobile) setShowLibrary(false);
  }, [isDark, isMobile, nodes, setSelectedNode]);

  return (
    <Box
      display="flex"
      gap={2}
      height="100%"
      flex={1}
      minHeight={0}
      flexDirection={{ xs: 'column', md: 'row' }}
      position="relative"
    >
      {showLibrary && (
        <Paper
          sx={{
            width: { xs: '100%', md: 300 },
            maxHeight: { xs: '40vh', md: 'none' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: { xs: 'relative', md: 'relative' },
            zIndex: { xs: 5, md: 'auto' },
          }}
        >
          <Box p={{ xs: 1.5, sm: 2 }} borderBottom={1} borderColor="divider">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant={isMobile ? 'body1' : 'h6'}>Expert Library</Typography>
              <Box display="flex" gap={0.5}>
                <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setExpertFormOpen(true)}>
                  New
                </Button>
              </Box>
            </Box>

            <TextField
              size="small"
              placeholder="Search experts..."
              fullWidth
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Box>

          <Box flex={1} overflow="auto" p={1}>
            {filteredExperts.length > 0 ? (
              <List dense>
                {filteredExperts.map((expert: ExpertListItem) => {
                  const isOnCanvas = nodes.some((node) => node.id === expert.id.toString());
                  const runtimeLabel = getExpertRuntimeLabel(expert);
                  return (
                    <ListItem key={expert.id} disablePadding sx={{ mb: 0.75 }}>
                      <ListItemButton
                        onClick={() => {
                          if (isOnCanvas) {
                            openExpertEditor(expert);
                            return;
                          }

                          addExpertToCanvas(expert);
                          if (isMobile) setShowLibrary(false);
                        }}
                        sx={{
                          alignItems: 'flex-start',
                          borderRadius: 1.25,
                          border: 1,
                          borderColor: isOnCanvas ? 'success.light' : 'divider',
                          backgroundColor: isOnCanvas ? 'success.50' : 'transparent',
                          py: 1,
                          px: 1.25,
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                          sx={{ m: 0, minWidth: 0 }}
                          primary={
                            <Box display="flex" alignItems="flex-start" gap={1} sx={{ minWidth: 0 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box display="flex" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" component="span" noWrap sx={{ minWidth: 0, fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                    {expert.name}
                                  </Typography>
                                  {isOnCanvas && <Chip label="On Canvas" size="small" color="success" sx={{ height: 18, flexShrink: 0 }} />}
                                </Box>
                              </Box>
                              <Tooltip title="Edit expert">
                                <IconButton
                                  size="small"
                                  aria-label={`Edit ${expert.name}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openExpertEditor(expert);
                                  }}
                                  sx={{ mt: -0.5, mr: -0.5 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                          secondary={
                            <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.75}>
                              <Chip
                                label={expert.expert_type_display}
                                size="small"
                                color={getExpertTypeColor(expert.expert_type)}
                                sx={{ fontSize: '0.68rem', height: 18 }}
                              />
                              <Chip
                                label={`Model: ${runtimeLabel}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.68rem', height: 18, maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                              />
                              {expert.use_cache && (
                                <Chip
                                  label="Cache"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ fontSize: '0.68rem', height: 18 }}
                                />
                              )}
                              {expert.use_evaluator && (
                                <Chip
                                  label="Evaluator"
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ fontSize: '0.68rem', height: 18 }}
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Box p={2} textAlign="center">
                {searchTerm ? (
                  <Typography variant="body2" color="text.secondary">
                    No experts match "{searchTerm}"
                  </Typography>
                ) : (
                  <Alert severity="info">No experts available. Click "New" to create your first expert!</Alert>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      )}

      <Box flex={1} position="relative" minHeight={{ xs: '300px', sm: '400px' }}>
        <Paper
          sx={{
            height: '100%',
            position: 'relative',
            bgcolor: isDark ? '#1a1a1a' : 'background.paper',
            ...(isDark && {
              '& .react-flow__controls': {
                borderColor: '#555',
                boxShadow: '0 0 4px rgba(0,0,0,0.5)',
              },
              '& .react-flow__controls-button': {
                backgroundColor: '#2a2a2a',
                borderColor: '#555',
                fill: '#e0e0e0',
                '&:hover': {
                  backgroundColor: '#3a3a3a',
                },
              },
              '& .react-flow__attribution': {
                backgroundColor: 'rgba(30, 30, 30, 0.7)',
                '& a': {
                  color: '#888',
                },
              },
            }),
          }}
        >
          <ReactFlow
            nodes={nodes.map((node) => ({
              ...node,
              data: {
                ...node.data,
                isDark,
                runState: expertRunStates?.[node.id],
                onViewExpertRun,
              },
            }))}
            edges={routedEdges.map((edge) => ({
              ...edge,
              type: edge.type || 'deletable',
              style: { ...(edge.selected ? selectedEdgeStyle : edge.style || defaultEdgeOptions.style),
                opacity: hoveredNodeId && edge.source !== hoveredNodeId && edge.target !== hoveredNodeId ? 0.12 : 1 },
              ariaLabel: `${nodes.find(node => node.id === edge.source)?.data?.expert?.name || edge.source} to ${nodes.find(node => node.id === edge.target)?.data?.expert?.name || edge.target}`,
              markerEnd: edge.selected ? createEdgeMarker(SELECTED_EDGE_COLOR) : edge.markerEnd || createEdgeMarker(DEFAULT_EDGE_COLOR),
            }))}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onNodeMouseEnter={(_event, node) => setHoveredNodeId(node.id)}
            onNodeMouseLeave={() => setHoveredNodeId(null)}
            onInit={onInit}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            deleteKeyCode={['Delete', 'Backspace']}
            multiSelectionKeyCode="Shift"
            panActivationKeyCode={null}
            elementsSelectable
            selectNodesOnDrag={false}
            fitView
            fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
            proOptions={{ hideAttribution: true }}
            minZoom={0.1}
            maxZoom={2}
            panOnDrag
            panOnScroll={false}
            zoomOnScroll
            zoomOnPinch
          >
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} color={isDark ? '#555' : undefined} />
            {!isMobile && (
              <MiniMap
                nodeColor={(node) => {
                  const expert = (node.data as any)?.expert;
                  return expert?.expert_type === 'ANALYSIS' ? '#1976d2' : '#9c27b0';
                }}
                style={{
                  width: 120,
                  height: 90,
                  backgroundColor: isDark ? '#1e1e1e' : '#f5f5f5',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
                maskColor={isDark ? 'rgba(0, 0, 0, 0.6)' : undefined}
              />
            )}

            <Panel position="top-left">
              <Paper sx={{ p: 0.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setExpertFormOpen(true)}
                  sx={{ minHeight: 30, whiteSpace: 'nowrap' }}
                >
                  Expert
                </Button>
                {(!isMobile || !showLibrary) && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowLibrary((current) => !current)}
                    startIcon={showLibrary ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    sx={{ mr: 0.25 }}
                  >
                    {showLibrary ? 'Hide Library' : 'Library'}
                  </Button>
                )}
                <ButtonGroup size="small" variant="outlined">
                  <Button onClick={() => { onAutoLayout(); setTimeout(handleFitView, 150); }} sx={{ minWidth: 36, px: 1 }} aria-label="Auto layout" title="Arrange flow from top to bottom">
                    <AutoLayoutIcon fontSize="small" />
                  </Button>
                  <Button onClick={handleZoomIn} sx={{ minWidth: 36, px: 1 }} aria-label="Zoom in">
                    <ZoomInIcon fontSize="small" />
                  </Button>
                  <Button onClick={handleZoomOut} sx={{ minWidth: 36, px: 1 }} aria-label="Zoom out">
                    <ZoomOutIcon fontSize="small" />
                  </Button>
                  <Button onClick={handleFitView} sx={{ minWidth: 36, px: 1 }} aria-label="Fit canvas">
                    <FitScreenIcon fontSize="small" />
                  </Button>
                </ButtonGroup>
                {onSaveCanvas && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={onSaveCanvas}
                    disabled={isSavingCanvas}
                    startIcon={isSavingCanvas ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
                    sx={{ minHeight: 30, whiteSpace: 'nowrap' }}
                  >
                    Save
                  </Button>
                )}
                {onRunCanvas && (
                  <Tooltip title={runDisabledReason || (isRunningCanvas ? 'Manual test in progress' : 'Test this AI system')}>
                    <span>
                      <Button
                        size="small"
                        variant={isRunningCanvas ? 'outlined' : 'contained'}
                        color={isRunningCanvas ? 'primary' : 'success'}
                        onClick={onRunCanvas}
                        disabled={isRunningCanvas || !canRunCanvas}
                        startIcon={isRunningCanvas ? <CircularProgress size={14} color="inherit" /> : <RunIcon fontSize="small" />}
                        sx={{ minHeight: 30, whiteSpace: 'nowrap' }}
                      >
                        {isRunningCanvas ? 'Running' : 'Test'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {toolbarActions}
              </Paper>
            </Panel>

            {(!isMobile || validationIssues.length > 0) && (
              <Panel position="bottom-left">
                <Box display="flex" flexDirection="column" gap={1} alignItems="flex-start">
                  {validationIssues.length > 0 && (
                    <Paper sx={{ p: 1, maxWidth: { xs: 280, sm: 400 }, bgcolor: isDark ? 'grey.900' : 'background.paper' }}>
                      <Typography variant="caption" display="block" fontWeight="bold" mb={0.5}>
                        <Badge badgeContent={validationIssues.length} color="error" sx={{ mr: 2 }}>
                          Issues
                        </Badge>
                      </Typography>
                      <Box maxHeight={{ xs: 80, sm: 150 }} overflow="auto">
                        {validationIssues.slice(0, isMobile ? 2 : validationIssues.length).map((issue, index) => (
                          <Alert key={index} severity={issue.type} sx={{ mb: 0.5, py: 0, fontSize: '0.75rem' }}>
                            <Typography variant="caption">{issue.message}</Typography>
                          </Alert>
                        ))}
                        {isMobile && validationIssues.length > 2 && (
                          <Typography variant="caption" color="text.secondary">
                            +{validationIssues.length - 2} more issues
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  )}

                  {!isMobile && (
                    <Paper
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        bgcolor: isDark ? 'grey.900' : 'background.paper',
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                        Workflow Statistics
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                          Experts: <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>{nodes.length}</Box>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                          Connections: <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>{edges.length}</Box>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                          Analysis: <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>{nodes.filter((node) => (node.data as any)?.expert?.expert_type === 'ANALYSIS').length}</Box>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                          Action: <Box component="span" sx={{ color: 'secondary.main', fontWeight: 700 }}>{nodes.filter((node) => (node.data as any)?.expert?.expert_type === 'ACTION').length}</Box>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                          Packets: <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>{nodes.filter((node) => Boolean((node.data as any)?.expert?.input_data?.runtime_input_packet)).length}/{nodes.length}</Box>
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Box>
              </Panel>
            )}
          </ReactFlow>
        </Paper>
      </Box>

      {selectedNode && (
        <ExpertEditorPanel
          selectedNode={selectedNode}
          edges={edges}
          expertsData={expertsData}
          onClose={() => setSelectedNode(null)}
          onUpdate={async (updatedExpert) => {
            if (updatedExpert === null) {
              setNodes((currentNodes) => currentNodes.filter((node) => node.id !== selectedNode.id));
              setEdges((currentEdges) => currentEdges.filter((edge) => edge.source !== selectedNode.id && edge.target !== selectedNode.id));
              setSelectedNode(null);
            } else {
              let hasIndicatorSets = false;
              let indicatorSetCount = 0;

              try {
                const fullExpert = await expertApi.get(updatedExpert.id);
                const inputs = fullExpert.input_data?.inputs || [];
                const indicatorSetInputs = inputs.filter((input: any) => input.type === 'indicator' && (input.use_indicator_set || input.indicator_set_id));

                hasIndicatorSets = indicatorSetInputs.length > 0;
                indicatorSetCount = indicatorSetInputs.length;
              } catch (error) {
                console.warn('Could not fetch updated expert details:', error);
              }

              setNodes((currentNodes) =>
                currentNodes.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        expert: updatedExpert,
                        hasIndicatorSets,
                        indicatorSetCount,
                        label: (
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {updatedExpert.name}
                            </Typography>
                            <Chip
                              label={updatedExpert.expert_type}
                              size="small"
                              color={updatedExpert.expert_type === 'ANALYSIS' ? 'primary' : 'secondary'}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        ),
                      },
                    };
                  }
                  return node;
                })
              );
            }
            refetchExperts();
          }}
        />
      )}
    </Box>
  );
};

export default AISystemCanvas;
